import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CompanyIdService {
  private readonly SALT = 'otto-website-builder-2024';
  
  constructor() {}

  /**
   * Encode a company GUID with salt and timestamp for URL usage
   */
  encode(companyGuid: string): string {
    console.log('CompanyIdService: Encoding GUID:', companyGuid);
    
    try {
      // Simplified encoding - just encode the GUID directly
      const payload = `${companyGuid}`;
      const encoded = btoa(payload);
      
      console.log('CompanyIdService: Encoded payload:', payload);
      console.log('CompanyIdService: Final encoded result:', encoded);
      
      return encoded;
    } catch (error) {
      console.error('CompanyIdService: Failed to encode company ID:', error);
      return '';
    }
  }

  /**
   * Decode an encoded company ID and extract just the company GUID
   * Handles both new format (just GUID) and legacy format (salt:guid:timestamp)
   */
  decode(encodedId: string): string | null {
    try {
      const decoded = atob(encodedId);
      
      // Check if this is a legacy format with salt and timestamp
      if (decoded.includes(':')) {
        const parts = decoded.split(':');
        if (parts.length === 3 && parts[0] === this.SALT) {
          // Legacy format: salt:guid:timestamp
          return parts[1]; // Return just the GUID
        }
      }
      
      // New format: just the GUID
      return decoded;
    } catch (error) {
      console.error('CompanyIdService: Failed to decode company ID:', error);
      return null;
    }
  }

  /**
   * Extract company GUID from any encoded ID format (legacy or new)
   * This is the preferred method to ensure consistent GUID extraction
   */
  extractCompanyGuid(encodedId: string): string | null {
    return this.decode(encodedId);
  }

  /**
   * Validate that an encoded ID is properly formatted
   */
  isValidEncodedId(encodedId: string): boolean {
    return this.decode(encodedId) !== null;
  }

  /**
   * Get encoding info for debugging (without revealing the GUID)
   */
  getEncodingInfo(encodedId: string): { isValid: boolean; timestamp?: number; age?: string; format: 'legacy' | 'new' } {
    try {
      const decoded = atob(encodedId);
      
      // Check if this is legacy format
      if (decoded.includes(':')) {
        const parts = decoded.split(':');
        if (parts.length === 3 && parts[0] === this.SALT) {
          const timestamp = parseInt(parts[2]);
          const age = this.formatAge(Date.now() - timestamp);
          
          return {
            isValid: true,
            timestamp,
            age,
            format: 'legacy'
          };
        }
      }
      
      // New format
      return {
        isValid: true,
        format: 'new'
      };
    } catch (error) {
      return { isValid: false, format: 'new' };
    }
  }

  private formatAge(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day(s)`;
    if (hours > 0) return `${hours} hour(s)`;
    if (minutes > 0) return `${minutes} minute(s)`;
    return `${seconds} second(s)`;
  }
} 